import { Suspense, useEffect, useState } from "react";
import {
  IMaintenanceHistoryItem,
  MaintenanceHistoryTableProps,
} from "../../../../types/maintenance.type";
import Loading from "../../../../common/components/ui/loading/Loading";
import TableFooter from "../../../../common/components/ui/table/TableFooter";
import {
  formatDate,
  formatDateAndTime,
} from "../../../../common/helpers/date.helpers";
import { getMaintenanceHistory } from "@/common/services/maintenance.api";
import { useNavigate } from "react-router-dom";

const typeOptions = [
  { value: "preventive", label: "Preventiva" },
  { value: "corrective", label: "Corretiva" },
  { value: "misuse", label: "Mau uso" },
];

function calculateDayDiff(dateStr1: string, dateStr2: string): string | number {
  try {
    const date1 = new Date(dateStr1);
    const date2 = new Date(dateStr2);

    if (isNaN(date1.getTime()) || isNaN(date2.getTime())) {
      return "-";
    }

    date1.setHours(0, 0, 0, 0);
    date2.setHours(0, 0, 0, 0);

    const diffTime = date1.getTime() - date2.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    return diffDays >= 0 ? diffDays : "-";
  } catch {
    return "-";
  }
}

function MaintenanceHistoryTable({
  licensePlate,
  areActionsDisabled,
  onHistoryChange,
  onFullHistoryUpdate,
}: MaintenanceHistoryTableProps) {
  const navigate = useNavigate();
  const [history, setHistory] = useState<IMaintenanceHistoryItem[]>([]);
  const [originalHistory, setOriginalHistory] = useState<
    IMaintenanceHistoryItem[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [paginationData, setPaginationData] = useState({
    itemsPerPage: 10,
    totalItems: 0,
  });

  useEffect(() => {
    async function loadHistory() {
      if (!licensePlate) return;

      setIsLoading(true);
      const result = await getMaintenanceHistory(licensePlate);

      if (result.ok) {
        const data = result.data as IMaintenanceHistoryItem[];
        const formattedData = data.map((item) => ({
          ...item,
          newType: item.type,
        }));
        setHistory(formattedData);
        setOriginalHistory(formattedData);
        onFullHistoryUpdate(formattedData);
        setPaginationData({
          itemsPerPage: 10,
          totalItems: result.totalItems,
        });
      } else {
        console.error("Falha ao buscar histórico de manutenção.");
        setHistory([]);
        setOriginalHistory([]);
      }
      setIsLoading(false);
    }

    loadHistory();
  }, [licensePlate, page, onFullHistoryUpdate]);

  const handleTypeChange = (
    itemName: string,
    newType: "corrective" | "preventive" | "misuse"
  ) => {
    const updatedHistory = history.map((item) =>
      item.partReplaced === itemName
        ? { ...item, type: newType, newType: newType }
        : item
    );
    setHistory(updatedHistory);
    onFullHistoryUpdate(updatedHistory);
    const changedItems = updatedHistory.filter((item) => {
      const originalItem = originalHistory.find(
        (original) => original.partReplaced === item.partReplaced
      );
      if (!originalItem) {
        return true;
      }
      return originalItem.type !== item.type;
    });

    const allChangesWithDetails: IMaintenanceHistoryItem[] = changedItems.map(
      (item) => {
        const originalItem = originalHistory.find(
          (original) => original.partReplaced === item.partReplaced
        );

        return {
          ...item,
          newType: item.type,
          previousType: originalItem ? originalItem.type : undefined,
        };
      }
    );

    onHistoryChange(allChangesWithDetails);
  };

  const maintenanceInfo = history.length > 0 ? history[0] : null;

  return (
    <Suspense fallback={<Loading />}>
      <div className="p-4">
        {maintenanceInfo ? (
          <div className="mb-4">
            <p className="text-base text-gray-600">
              <strong>Data:</strong>{" "}
              {formatDateAndTime(maintenanceInfo.maintenanceDate)}
            </p>
            <p className="text-base text-gray-600">
              <strong>Placa:</strong>{" "}
              <a
                className="text-blue-600 hover:text-bluePrimary cursor-pointer"
                onClick={() => navigate(`/veiculos/${licensePlate}`)}
                target="_blank"
              >
                {licensePlate}
              </a>
            </p>
            <p className="text-base text-gray-600">
              <strong>Hodômetro:</strong>{" "}
              {maintenanceInfo.kmAtReplacement.toLocaleString("pt-BR")}
            </p>
          </div>
        ) : (
          <h2 className="text-xl font-semibold mb-4">
            Histórico de Manutenção - Placa: {licensePlate}
          </h2>
        )}
        <div className="generic-table">
          <table className="generic-table-style">
            <thead className="generic-table-thead">
              <tr>
                <th className="generic-table-header">Peça Trocada</th>
                <th className="generic-table-header">Tipo</th>
                <th className="generic-table-header">Quantidade</th>
                <th className="generic-table-header">Data Última Troca</th>
                <th className="generic-table-header">KM Última Troca</th>
                <th className="generic-table-header">Dif dias</th>
                <th className="generic-table-header">Dif Hodômetro</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {!isLoading && history.length > 0 ? (
                history.map((item, index) => {
                  let diffDays: string | number = "-";
                  let diffKm: string | number = "-";

                  if (maintenanceInfo) {
                    if (item.lastReplacementDate) {
                      diffDays = calculateDayDiff(
                        maintenanceInfo.maintenanceDate,
                        item.lastReplacementDate
                      );
                    }

                    if (
                      item.kmAtLastReplacement !== null &&
                      item.kmAtLastReplacement !== undefined
                    ) {
                      const currentKm = Number(maintenanceInfo.kmAtReplacement);
                      const lastKm = Number(item.kmAtLastReplacement);
                      if (
                        typeof currentKm === "number" &&
                        typeof lastKm === "number"
                      ) {
                        diffKm = (currentKm - lastKm).toLocaleString("pt-BR");
                      }
                    }
                  }

                  return (
                    <tr
                      key={item.partReplaced + index}
                      className={`${
                        index % 2 ? "bg-gray2/10" : "bg-white"
                      } text-black`}
                    >
                      <td className="generic-table-th">{item.partReplaced}</td>
                      <td className="generic-table-th">
                        <select
                          value={item.type}
                          onChange={(e) =>
                            handleTypeChange(
                              item.partReplaced,
                              e.target.value as
                                | "corrective"
                                | "preventive"
                                | "misuse"
                            )
                          }
                          disabled={areActionsDisabled}
                          className="border border-gray-300 rounded-md p-2"
                        >
                          {typeOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="generic-table-th">{item.quantity}</td>
                      <td className="generic-table-th">
                        {item.lastReplacementDate
                          ? formatDate(item.lastReplacementDate)
                          : "-"}
                      </td>
                      <td className="generic-table-th">
                        {item.kmAtLastReplacement
                          ? (item.kmAtLastReplacement as number).toLocaleString(
                              "pt-BR"
                            )
                          : "-"}
                      </td>
                      <td className="generic-table-th">{diffDays}</td>
                      <td className="generic-table-th">{diffKm}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td className="py-3 text-center" colSpan={7}>
                    {isLoading
                      ? "Carregando..."
                      : "Nenhum registro encontrado."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {!isLoading && history.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            Nenhum histórico de peças encontrado para a última manutenção
            finalizada.
          </div>
        )}
        <TableFooter
          page={page}
          totalItems={paginationData.totalItems}
          itemsPerPage={paginationData.itemsPerPage}
          goToPage={setPage}
        />
      </div>
    </Suspense>
  );
}

export default MaintenanceHistoryTable;
